import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MensajeError } from "../../componentes/FuncionesLocales";
import { useStoreHome } from "../Home/useStoreHome";
import {
  obtenerVentasDetalle,
  obtenerVentasEstados,
  obtenerOpcionesFiltrosVentas,
  obtenerVentasRanking,
  obtenerVentasResumen,
} from "../../servicios/ventasReportesApi";
import { calcularPaginasVentas, crearOpcionesFiltrosVentas, normalizarEstadosVentas, normalizarRankingVentas } from "../../servicios/ventasReportesUtils";

const fechaLocal = (fecha) => {
  const copia = new Date(fecha);
  copia.setMinutes(copia.getMinutes() - copia.getTimezoneOffset());
  return copia.toISOString().slice(0, 10);
};

const hoy = new Date();
const haceTreintaDias = new Date();
haceTreintaDias.setDate(hoy.getDate() - 29);

export const FILTROS_VENTAS_INICIALES = {
  FechaDesde: fechaLocal(haceTreintaDias),
  FechaHasta: fechaLocal(hoy),
  ClienteId: null,
  MercaderiaId: null,
  OperadorId: null,
  Estado: "",
  FormaPago: "",
  Pagina: 1,
  TamanoPagina: 25,
};

const DETALLE_VACIO = { Lista: [], TotalRegistros: 0, Pagina: 1, TamanoPagina: 25 };
const OPCIONES_FILTROS_VACIAS = crearOpcionesFiltrosVentas();

export const useVentasReportes = () => {
  const EmpresaId = useStoreHome((state) => state.EmpresaId);
  const [filtros, setFiltros] = useState(FILTROS_VENTAS_INICIALES);
  const [resumen, setResumen] = useState(null);
  const [detalle, setDetalle] = useState(DETALLE_VACIO);
  const [ranking, setRanking] = useState({ Lista: [], AgrupadoPor: "Producto" });
  const [estados, setEstados] = useState([]);
  const [agruparPor, setAgruparPor] = useState("Producto");
  const [cargando, setCargando] = useState(false);
  const [opcionesFiltros, setOpcionesFiltros] = useState(OPCIONES_FILTROS_VACIAS);
  const [cargandoOpciones, setCargandoOpciones] = useState(false);
  const consultandoRef = useRef(false);
  const conexionInicialRef = useRef(null);
  const filtrosRef = useRef(filtros);

  useEffect(() => {
    filtrosRef.current = filtros;
  }, [filtros]);

  const request = useMemo(() => ({ ...filtros, EmpresaId }), [filtros, EmpresaId]);

  const aplicarRespuesta = useCallback((respuesta, setter, valorPorDefecto) => {
    if (respuesta.error) {
      MensajeError(respuesta.error);
      return false;
    }
    setter(respuesta.data ?? valorPorDefecto);
    return true;
  }, []);

  const ejecutarConCarga = useCallback(async (operacion) => {
    if (consultandoRef.current) return;
    consultandoRef.current = true;
    setCargando(true);
    try {
      await operacion();
    } finally {
      consultandoRef.current = false;
      setCargando(false);
    }
  }, []);

  const cargarReportes = useCallback(async () => {
    if (!EmpresaId) return;
    await ejecutarConCarga(async () => {
      const [rResumen, rDetalle, rRanking, rEstados] = await Promise.all([
        obtenerVentasResumen(request),
        obtenerVentasDetalle(request),
        obtenerVentasRanking(request, agruparPor),
        obtenerVentasEstados(request),
      ]);
      aplicarRespuesta(rResumen, setResumen, null);
      aplicarRespuesta(rDetalle, setDetalle, { ...DETALLE_VACIO, TamanoPagina: filtros.TamanoPagina });
      aplicarRespuesta(rRanking, setRanking, { Lista: [], AgrupadoPor: agruparPor });
      if (rEstados.error) MensajeError(rEstados.error);
      else setEstados(rEstados.data?.Embudo || []);
    });
  }, [agruparPor, aplicarRespuesta, ejecutarConCarga, filtros.TamanoPagina, request, EmpresaId]);

  useEffect(() => {
    if (!EmpresaId || cargando || conexionInicialRef.current === EmpresaId) return;
    conexionInicialRef.current = EmpresaId;
    cargarReportes();
  }, [cargando, cargarReportes, EmpresaId]);

  useEffect(() => {
    if (!EmpresaId) return undefined;
    let vigente = true;
    setCargandoOpciones(true);
    obtenerOpcionesFiltrosVentas(EmpresaId)
      .then((respuesta) => {
        if (!vigente) return;
        setOpcionesFiltros(crearOpcionesFiltrosVentas(respuesta.data));
        respuesta.errores.forEach(({ catalogo }) => {
          MensajeError(`No se pudieron cargar las opciones de ${catalogo}.`);
        });
      })
      .catch(() => {
        if (vigente) MensajeError("No se pudieron cargar las opciones de los filtros de ventas.");
      })
      .finally(() => {
        if (vigente) setCargandoOpciones(false);
      });
    return () => { vigente = false; };
  }, [EmpresaId]);

  const cambiarFiltro = useCallback((campo, valor) => {
    const opcionalNumerico = ["ClienteId", "MercaderiaId", "OperadorId"].includes(campo);
    setFiltros((actual) => ({
      ...actual,
      [campo]: opcionalNumerico ? (valor === "" ? null : Number(valor)) : valor,
      Pagina: 1,
    }));
  }, []);

  const limpiarFiltros = useCallback(() => {
    setFiltros({ ...FILTROS_VENTAS_INICIALES });
  }, []);

  const cambiarPagina = useCallback(async (pagina) => {
    const nuevos = { ...filtros, Pagina: pagina };
    setFiltros(nuevos);
    await ejecutarConCarga(async () => {
      const respuesta = await obtenerVentasDetalle({ ...nuevos, EmpresaId });
      aplicarRespuesta(respuesta, setDetalle, detalle);
    });
  }, [aplicarRespuesta, detalle, ejecutarConCarga, filtros, EmpresaId]);

  const cambiarTamanoPagina = useCallback(async (tamano) => {
    const nuevos = { ...filtros, Pagina: 1, TamanoPagina: tamano };
    setFiltros(nuevos);
    await ejecutarConCarga(async () => {
      const respuesta = await obtenerVentasDetalle({ ...nuevos, EmpresaId });
      aplicarRespuesta(respuesta, setDetalle, detalle);
    });
  }, [aplicarRespuesta, detalle, ejecutarConCarga, filtros, EmpresaId]);

  const cambiarAgrupacion = useCallback(async (valor) => {
    setAgruparPor(valor);
    const nuevos = { ...filtrosRef.current, Pagina: 1 };
    setFiltros(nuevos);
    await ejecutarConCarga(async () => {
      const respuesta = await obtenerVentasRanking({ ...nuevos, EmpresaId }, valor);
      aplicarRespuesta(respuesta, setRanking, { Lista: [], AgrupadoPor: valor });
    });
  }, [aplicarRespuesta, ejecutarConCarga, EmpresaId]);

  const paginas = calcularPaginasVentas(detalle.TotalRegistros, filtros.TamanoPagina);
  const rankingNormalizado = useMemo(() => normalizarRankingVentas(ranking.Lista), [ranking.Lista]);
  const estadosNormalizados = useMemo(() => normalizarEstadosVentas(estados), [estados]);

  return {
    filtros,
    resumen,
    detalle,
    ranking: rankingNormalizado,
    estados: estadosNormalizados,
    agruparPor,
    cargando,
    opcionesFiltros,
    cargandoOpciones,
    paginas,
    cargarReportes,
    limpiarFiltros,
    cambiarFiltro,
    cambiarPagina,
    cambiarTamanoPagina,
    cambiarAgrupacion,
  };
};
